const { __ } = wp.i18n;

/**
 * Takes the result image data from the api response,
 * handles it and returns an object with url, id, and alt properties.
 *
 * @param {Object} resultImage - The results image object containing url, width, height etc
 * @returns {Object} Object containing url, id, and alt properties extracted from the result image
 */
export const resultImageHelper = (resultImage) => {
	let url = '';
	let id = '';
	let alt = '';

	// Create an array from resultImage object
	const imgAttributes = Object.keys(resultImage);

	// Conditional to check image exists and array is popluated before output the element
	if (imgAttributes.length > 0) {
		// Loop over array and set each item as an attribute
		imgAttributes.forEach((key) => {
			// If key = url set the variable
			if (key === 'url') {
				url = resultImage[key];
			}
			// If key = url set the variable
			if (key === 'id') {
				id = resultImage[key];
			}
			// If key = url set the variable
			if (key === 'alt') {
				alt = resultImage[key];
			}
		});
	}
	return {
		url,
		id,
		alt,
	};
};

/**
 * Copies the page URL to the users clipboard and displays an alert.
 * @param {Event} event - The event object from the triggering action
 * @returns {void}
 */
export const copyURLToClipboard = (event) => {
	event.preventDefault();

	// Create a share URL that strips all params and adds '?shared'.
	const currentHref = window.location.href;
	const currentUrl = new URL(currentHref);
	const shareUrl = `${currentUrl.origin}${currentUrl.pathname}?shared`;

	navigator.clipboard.writeText(shareUrl).then(() => {
		// Alert user.
		const alert = document.querySelector('.mavisQuiz_shareAlert');
		const button = document.querySelector('.mavisQuiz_shareAlert-close');

		// Show alert & focus onto 'close' button for easy keyboard use.
		alert.classList.remove('is-hidden');
		button.focus();

		// On either click or when 'enter' is pressed, hide the alert again.
		button.addEventListener('click', (e) => {
			e.preventDefault();
			alert.classList.add('is-hidden');
		});
		button.addEventListener('keydown', (e) => {
			e.preventDefault();
			if (e.keyCode === 13) {
				alert.classList.add('is-hidden');
			}
		});
	});
};

/**
 * Alert box when the user shares a quiz with friends.
 */
export const quizAlert = `
<div class="mavisQuiz_shareAlert is-hidden">
  <p>${__(
		'The link to this page has been copied to your clipboard so you can share it with your friends',
		'mavis',
	)}</p>
  <button class="mavisQuiz_shareAlert-close">${__('close', 'mavis')}</button>
</div>`;

/**
 * Checks if the dataLayer exists on the window and pushes the data if it can.
 * @param {object} obj - Object of data to push to datalayer.
 */
export const dataLayerPush = (obj) => {
	if (window.dataLayer) {
		window.dataLayer.push(obj);
	}
};

/**
 * Manipulates the behaviour of arrow keys on radio groups so they can still be used for navigation, but they don't change the radio selection.
 * @param {Array<HTMLFieldSetElement>} fieldsets - Array of fieldsets to apply the behaviour to.
 */
export const radioGroupArrowKeyNavigation = (fieldsets) => {
	fieldsets.forEach((fieldset) => {
		const inputEls = fieldset.querySelectorAll('input[type="radio"]');
		if (!!inputEls.length === false) return;
		inputEls.forEach((input, index) => {
			input.addEventListener('keydown', (event) => {
				if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
					event.preventDefault();
					const nextInput = index === inputEls.length - 1 ? inputEls[0] : inputEls[index + 1];
					nextInput.focus();
				} else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
					event.preventDefault();
					const prevInput = index === 0 ? inputEls[inputEls.length - 1] : inputEls[index - 1];
					prevInput.focus();
				}
			});
		});
	});
};

/**
 * Triggers the Netflix-style next quiz popup with progress bar and auto-redirect.
 *
 * @param {HTMLElement} quizWrapper - The main quiz wrapper element.
 * @param {string} resultsSelector - Class selector for the results container.
 */
export const triggerNextQuizPopup = (quizWrapper, resultsSelector) => {
	const popup = quizWrapper.querySelector('#mavis-next-quiz-popup');
	if (!popup) return;

	const progressFill = popup.querySelector('.mavis-progress-fill');
	const progressBar = popup.querySelector('[role="progressbar"]');
	const liveRegion = popup.querySelector('#mavis-next-live-region');
	const cancelButton = popup.querySelector('#mavis-cancel-next');
	const nextUrl = popup.dataset.autoUrl;

	let previouslyFocusedElement = null;
	let countdownInterval = null;
	let closePopup;

	/**
	 * Gets all focusable elements within the popup.
	 * @returns {NodeList} NodeList of focusable elements
	 */
	const getFocusableElements = () =>
		popup.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');

	/**
	 * Traps focus within the popup dialog for keyboard accessibility.
	 * @param {KeyboardEvent} e - The keyboard event
	 * @returns {void}
	 */
	const trapFocus = (e) => {
		if (e.key !== 'Tab') return;

		const focusable = getFocusableElements();
		if (!focusable.length) return;

		const first = focusable[0];
		const last = focusable[focusable.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	};

	/**
	 * Unified handler for popup keyboard interactions.
	 * Handles closing on Escape and trapping focus on Tab.
	 * @param {KeyboardEvent} e - The keyboard event
	 */
	const handleKeydown = (e) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			if (closePopup) closePopup(true);
			return;
		}
		trapFocus(e);
	};

	/**
	 * Closes the next quiz popup and cleans up timers and event listeners.
	 * @param {boolean} [announce=false] - Whether to announce the cancellation to screen readers
	 * @returns {void}
	 */
	closePopup = (announce = false) => {
		clearTimeout(popup.dataset.redirectTimerId);
		clearInterval(countdownInterval);

		popup.style.display = 'none';

		// Remove the unified listener
		popup.removeEventListener('keydown', handleKeydown);

		if (announce && liveRegion) {
			liveRegion.textContent = 'Next quiz redirect cancelled.';
		}

		if (previouslyFocusedElement) {
			previouslyFocusedElement.focus();
		}
	};

	// Wait 3 seconds after results appear before showing popup
	const showPopupTimer = setTimeout(() => {
		const results = quizWrapper.querySelector(resultsSelector);
		if (!results || !results.classList.contains('is-active')) return;

		previouslyFocusedElement = document.activeElement;

		// Show popup
		popup.style.display = 'flex';

		// Move focus into dialog
		(cancelButton || popup).focus();

		// Announce popup
		if (liveRegion) {
			liveRegion.textContent = 'Up next. Next quiz starting in 15 seconds.';
		}

		// Trap focus + escape handling
		popup.addEventListener('keydown', handleKeydown);

		// Animate progress bar (15 seconds)
		const countdownTime = 15000;
		const startTime = Date.now();

		requestAnimationFrame(() => {
			if (progressFill) {
				progressFill.style.transition = `width ${countdownTime}ms linear`;
				progressFill.style.width = '100%';
			}
		});

		// Update aria-valuenow + announcements
		countdownInterval = setInterval(() => {
			const elapsed = Date.now() - startTime;
			const remaining = Math.max(0, countdownTime - elapsed);
			const percent = Math.min(100, Math.round((elapsed / countdownTime) * 100));

			if (progressBar) {
				progressBar.setAttribute('aria-valuenow', percent.toString());
			}

			const secondsLeft = Math.ceil(remaining / 1000);

			if (liveRegion && secondsLeft === 5) {
				const message = 'Next quiz starting in 5 seconds.';
				if (liveRegion.textContent !== message) {
					liveRegion.textContent = message;
				}
			}

			if (remaining <= 0) {
				clearInterval(countdownInterval);
			}
		}, 1000);

		// Redirect timer
		const redirectTimer = setTimeout(() => {
			if (nextUrl) {
				window.location.href = nextUrl;
			}
		}, countdownTime);

		popup.dataset.redirectTimerId = redirectTimer;
	}, 3000);

	popup.dataset.showTimerId = showPopupTimer;

	// Cancel button (one-time)
	if (cancelButton) {
		cancelButton.addEventListener(
			'click',
			(e) => {
				e.preventDefault();
				closePopup(true);
			},
			{ once: true },
		);
	}
};

/**
 * Resets the next quiz popup (clears timers, hides DOM).
 *
 * @param {HTMLElement} quizWrapper - The main quiz wrapper element containing the popup
 * @returns {void}
 */
export const resetNextQuizPopup = (quizWrapper) => {
	const popup = quizWrapper.querySelector('#mavis-next-quiz-popup');
	if (!popup) return;

	if (popup.dataset.showTimerId) clearTimeout(popup.dataset.showTimerId);
	if (popup.dataset.redirectTimerId) clearTimeout(popup.dataset.redirectTimerId);

	popup.style.display = 'none';

	const progressFill = popup.querySelector('.mavis-progress-fill');
	const progressBar = popup.querySelector('[role="progressbar"]');

	if (progressFill) {
		progressFill.style.transition = 'none';
		progressFill.style.width = '0%';
	}

	if (progressBar) {
		progressBar.setAttribute('aria-valuenow', '0');
	}
};
